import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { inputStyle } from '../components/FormField';
import * as orderApi from '../services/orderAnalysisApi';
import { formatDate } from '../utils/dateFormat';

const DIMENSIONS = [
  { key: 'customer', label: 'Customer' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'group', label: 'Group' },
  { key: 'refParty', label: 'Ref. Party' },
  { key: 'design', label: 'Design' },
  { key: 'baseDesign', label: 'Base Design' },
  { key: 'product', label: 'Product' },
  { key: 'category', label: 'Category' },
  { key: 'size', label: 'Size' },
];

// Shared vertical-divider table look (header row tinted + bold, a line
// between every column) — matches the Sales Map tables elsewhere in the
// app, used here for the two card detail lists specifically requested.
const BORDER = '1px solid var(--color-border)';
const thLined = { padding: '8px 4px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b', borderRight: BORDER };
const tdLined = { padding: '8px 4px', fontSize: 'var(--font-size-sm)', borderRight: BORDER };
const findBoxStyle = {
  width: '100%',
  marginTop: 4,
  padding: '4px 6px',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 400,
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  boxSizing: 'border-box',
};

/** Every distinct size name appearing across a list of order GROUPS' lines
 * (each line has one size_name, one pack_box) — becomes the dynamic
 * per-size columns for the Complete/Pending Order detail tables, sorted
 * the same natural-numeric way the Sales Map's size columns are, so a
 * size that doesn't appear anywhere in the current data never shows an
 * empty column, and a new size in the data appears automatically with no
 * code change. */
function distinctSizeNamesFromGroups(groups) {
  const names = new Set();
  groups.forEach((g) => g.lines.forEach((l) => names.add(l.size_name)));
  return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
}

function fmtDate(d) {
  return formatDate(d);
}

/** Filters order GROUPS by a set of find-box queries — group-level fields
 * (order date, complete date, order number, customer, ref. party) must
 * match the whole group; line-level fields (design, batch/shade) only
 * need ONE line in the group to match. A matching group is kept WHOLE
 * (every one of its lines still shown, not just the matching line) so the
 * rowSpan-merged table always shows a complete, real order rather than a
 * fragment — searching for one design still lets you see what else was
 * in that same order. */
function filterOrderGroups(groups, filters) {
  const q = (v) => v.trim().toLowerCase();
  return groups.filter((g) => {
    if (filters.orderDate && !fmtDate(g.orderDate).toLowerCase().includes(q(filters.orderDate))) return false;
    if (filters.completeDate && !fmtDate(g.completeDate).toLowerCase().includes(q(filters.completeDate))) return false;
    if (filters.orderNo && !String(g.orderNo || '').toLowerCase().includes(q(filters.orderNo))) return false;
    if (filters.customer && !(g.customerName || '').toLowerCase().includes(q(filters.customer))) return false;
    if (filters.refParty && !(g.refPartyName || '').toLowerCase().includes(q(filters.refParty))) return false;
    if (filters.design) {
      const dq = q(filters.design);
      if (!g.lines.some((l) => (l.design_name || '').toLowerCase().includes(dq))) return false;
    }
    if (filters.batchShade) {
      const bq = q(filters.batchShade);
      if (!g.lines.some((l) => (l.batch_shade || '').toLowerCase().includes(bq))) return false;
    }
    return true;
  });
}

/** A header cell with a label and a find box beneath it, for the
 * Complete/Pending Order detail tables — a plain controlled text input,
 * not a sortable header (these tables are ordered by the data itself, not
 * user-chosen sort). */
function FindableHeader({ label, value, onChange, lastColumn }) {
  return (
    <th style={lastColumn ? { ...thLined, borderRight: 'none' } : thLined}>
      {label}
      <input
        type="text"
        placeholder={`Find ${label.toLowerCase()}…`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={findBoxStyle}
      />
    </th>
  );
}

/** One "90+ / 180+ / 1 Year+ Days Inactive Customers" card — a customer
 * qualifies if their LAST dispatch (across every design they've ever
 * bought) is at least `thresholdDays` in the past. Each customer's full
 * historical (design, size) purchase breakdown is shown, rowSpan-merged
 * under their name/last-order-date/ref.party the same way the Complete/
 * Pending Order cards merge an order's lines — with a find box on every
 * column (Customer, Last Order Date, Design, Ref. Party). */
function InactiveCustomersCard({ title, thresholdDays }) {
  const [expanded, setExpanded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [customerQ, setCustomerQ] = useState('');
  const [lastOrderQ, setLastOrderQ] = useState('');
  const [designQ, setDesignQ] = useState('');
  const [refPartyQ, setRefPartyQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    orderApi.getInactiveCustomers(thresholdDays)
      .then((data) => { if (!cancelled) setCustomers(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [thresholdDays]);

  const filtered = React.useMemo(() => {
    const cq = customerQ.trim().toLowerCase();
    const lq = lastOrderQ.trim().toLowerCase();
    const dq = designQ.trim().toLowerCase();
    const rq = refPartyQ.trim().toLowerCase();
    return customers.filter((c) => {
      if (cq && !(c.customerName || '').toLowerCase().includes(cq)) return false;
      if (lq && !fmtDate(c.lastDispatchDate).toLowerCase().includes(lq)) return false;
      if (rq && !(c.refPartyName || '').toLowerCase().includes(rq)) return false;
      if (dq && !c.designLines.some((l) => (l.designName || '').toLowerCase().includes(dq))) return false;
      return true;
    });
  }, [customers, customerQ, lastOrderQ, designQ, refPartyQ]);

  const sizeNames = React.useMemo(() => {
    const names = new Set();
    filtered.forEach((c) => c.designLines.forEach((l) => names.add(l.sizeName)));
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }, [filtered]);

  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: expanded ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-4)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {title}{expanded ? ' \u25B2' : ' \u25BC'}
        </div>
        <div className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-warning)', marginTop: 4 }}>
          {loading ? '…' : customers.length}
        </div>
      </button>

      {expanded && (
        <div className="table-scroll" style={{ marginTop: 'var(--space-4)' }}>
          {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
          <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#e2e8f0' }}>
                <FindableHeader label="Customer" value={customerQ} onChange={setCustomerQ} />
                <FindableHeader label="Last Order Date" value={lastOrderQ} onChange={setLastOrderQ} />
                <FindableHeader label="Design" value={designQ} onChange={setDesignQ} />
                {sizeNames.map((s) => (
                  <th key={s} className="data-figure" style={thLined}>{s}</th>
                ))}
                <FindableHeader label="Ref. Party" value={refPartyQ} onChange={setRefPartyQ} lastColumn />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => c.designLines.map((line, idx) => (
                <tr key={`${c.customerId}-${idx}`} style={{ borderBottom: BORDER }}>
                  {idx === 0 && (
                    <>
                      <td rowSpan={c.designLines.length} style={{ ...tdLined, verticalAlign: 'top', fontWeight: 600 }}>{c.customerName}</td>
                      <td rowSpan={c.designLines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top' }}>
                        {fmtDate(c.lastDispatchDate)}
                        <div style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>{c.daysSinceLastDispatch}d ago</div>
                      </td>
                    </>
                  )}
                  <td style={tdLined}>{line.designName}</td>
                  {sizeNames.map((s) => (
                    <td key={s} className="data-figure" style={tdLined}>
                      {line.sizeName === s ? line.qty : 0}
                    </td>
                  ))}
                  {idx === 0 && (
                    <td rowSpan={c.designLines.length} style={{ ...tdLined, verticalAlign: 'top', borderRight: 'none' }}>{c.refPartyName || '—'}</td>
                  )}
                </tr>
              )))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4 + sizeNames.length} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                    {customers.length === 0 ? 'No inactive customers at this threshold.' : 'No rows match this search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 8 }}>
            Based on each customer&apos;s most recent dispatch, across their full dispatch
            history — a customer with no dispatch on file at all never appears here (there is
            no last-order date to measure from).
          </p>
        </div>
      )}
    </div>
  );
}

function TrendBadge({ direction }) {
  const map = {
    up: { symbol: '\u2191', color: 'var(--color-success)', label: 'Up' },
    down: { symbol: '\u2193', color: 'var(--color-danger)', label: 'Down' },
    flat: { symbol: '\u2192', color: 'var(--color-text-muted)', label: 'Flat' },
  };
  const t = map[direction] || map.flat;
  return (
    <span style={{ color: t.color, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
      {t.symbol} {t.label}
    </span>
  );
}

function SortableHeader({ label, sortKey, activeSort, onSort }) {
  const isActive = activeSort.key === sortKey;
  const arrow = isActive ? (activeSort.direction === 'asc' ? ' \u2191' : ' \u2193') : '';
  return (
    <th
      role="button"
      tabIndex={0}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSort(sortKey); }}
      style={{
        padding: '8px 4px',
        fontSize: 'var(--font-size-xs)',
        color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
        cursor: 'pointer',
        userSelect: 'none',
        fontWeight: isActive ? 700 : 400,
      }}
    >
      {label}{arrow}
    </th>
  );
}


// A pending order line is one with no real production batch yet.
function isNoBatchLine(line) {
  const b = line.batch_shade;
  return !b || String(b).trim().toUpperCase() === '0/A';
}

/** "Design Need To Complete Pending Order": size cards -> click a size ->
 * the designs that still have to be produced in that size, how many boxes
 * and orders each one covers, and which orders it completes on its own
 * (the order's only unproduced line). Click a design for its order list. */
export function DesignNeedPanel({ groups }) {
  const [selSize, setSelSize] = React.useState('');
  const [openDesign, setOpenDesign] = React.useState('');
  const [openMode, setOpenMode] = React.useState('all'); // 'all' = every order covered, 'alone' = orders this design completes on its own
  const toggleDesign = (name, mode) => {
    if (openDesign === name && openMode === mode) setOpenDesign('');
    else { setOpenDesign(name); setOpenMode(mode); }
  };
  const detailRef = React.useRef(null);
  // when a size card is opened, bring its detail table into view
  React.useEffect(() => {
    if (selSize && detailRef.current) detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selSize]);

  const sizes = React.useMemo(() => {
    const out = {};
    groups.forEach((g) => {
      const missing = g.lines.filter(isNoBatchLine);
      const distinctMissing = new Set(missing.map((l) => `${l.size_name}|${l.design_name}`)).size;
      const orderKey = `${g.orderNo}|${g.customerId}`;
      missing.forEach((l) => {
        const s = l.size_name || '—';
        const d = l.design_name || '—';
        const sz = out[s] || (out[s] = { size: s, boxes: 0, orders: new Set(), designs: {} });
        const boxes = Number(l.pack_box || 0);
        sz.boxes += boxes;
        sz.orders.add(orderKey);
        const ds = sz.designs[d] || (sz.designs[d] = { design: d, boxes: 0, orders: {} });
        ds.boxes += boxes;
        const od = ds.orders[orderKey] || (ds.orders[orderKey] = {
          key: orderKey, orderNo: g.orderNo, customerName: g.customerName,
          orderDate: g.orderDate, boxes: 0, alone: distinctMissing === 1,
        });
        od.boxes += boxes;
      });
    });
    return Object.values(out).sort((a, b) => a.size.localeCompare(b.size, undefined, { numeric: true, sensitivity: 'base' }));
  }, [groups]);

  const sel = sizes.find((s) => s.size === selSize);
  const designs = sel
    ? Object.values(sel.designs)
        .map((d) => {
          const ords = Object.values(d.orders);
          return { ...d, ordList: ords, orderCount: ords.length, aloneCount: ords.filter((o) => o.alone).length };
        })
        .sort((a, b) => b.orderCount - a.orderCount || b.boxes - a.boxes)
    : [];

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, margin: '0 0 var(--space-2)' }}>
        Design Need To Complete Pending Order
      </div>
      {sizes.length === 0 && <p style={{ color: 'var(--color-text-muted)' }}>No pending order lines.</p>}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        {sizes.map((s) => {
          const on = s.size === selSize;
          return (
            <button
              key={s.size}
              type="button"
              onClick={() => { setSelSize(on ? '' : s.size); setOpenDesign(''); }}
              style={{
                background: on ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                border: on ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--space-3) var(--space-5)',
                minWidth: 170, cursor: 'pointer', textAlign: 'left', font: 'inherit',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>{s.size}</div>
              <div className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-warning)', marginTop: 4 }}>
                {s.orders.size} <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, color: 'var(--color-text-muted)' }}>orders</span>
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {Object.keys(s.designs).length} designs · {s.boxes.toLocaleString()} boxes
              </div>
            </button>
          );
        })}
      </div>

      {sel && <div ref={detailRef} style={{ scrollMarginTop: 70 }} />}
      {sel && (
        <Card title={`${sel.size} — designs needed to complete ${sel.orders.size} pending orders`}>
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#e2e8f0' }}>
                  <th style={thLined}>Design</th>
                  <th className="data-figure" style={thLined}>Boxes Needed</th>
                  <th className="data-figure" style={thLined}>Orders Covered</th>
                  <th className="data-figure" style={{ ...thLined, borderRight: 'none' }}>Completes Order Alone</th>
                </tr>
              </thead>
              <tbody>
                {designs.map((d) => (
                  <React.Fragment key={d.design}>
                    <tr
                      onClick={() => toggleDesign(d.design, 'all')}
                      style={{ borderBottom: BORDER, cursor: 'pointer', background: openDesign === d.design ? 'var(--color-accent-soft)' : undefined }}
                    >
                      <td style={{ ...tdLined, fontWeight: 600 }}>{openDesign === d.design ? '▾ ' : '▸ '}{d.design}</td>
                      <td className="data-figure" style={tdLined}>{d.boxes.toLocaleString()}</td>
                      <td className="data-figure" style={tdLined}>{d.orderCount}</td>
                      <td
                        className="data-figure"
                        title={d.aloneCount ? 'Click to see only the orders this design completes on its own' : undefined}
                        onClick={(e) => { e.stopPropagation(); if (d.aloneCount) toggleDesign(d.design, 'alone'); }}
                        style={{ ...tdLined, borderRight: 'none', color: d.aloneCount ? 'var(--color-success)' : undefined, fontWeight: d.aloneCount ? 700 : 400, cursor: d.aloneCount ? 'pointer' : 'default', textDecoration: d.aloneCount ? 'underline' : 'none' }}
                      >
                        {d.aloneCount}
                      </td>
                    </tr>
                    {openDesign === d.design && (
                      <tr>
                        <td colSpan={4} style={{ padding: 'var(--space-2) var(--space-4)', background: 'var(--color-surface)' }}>
                          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, margin: '4px 0 6px', color: openMode === 'alone' ? 'var(--color-success)' : 'var(--color-text-primary)' }}>
                            {openMode === 'alone'
                              ? `${d.aloneCount} order${d.aloneCount === 1 ? '' : 's'} completed by ${d.design} alone`
                              : `${d.orderCount} order${d.orderCount === 1 ? '' : 's'} covered by ${d.design}`}
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
                            <thead>
                              <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#f1f5f9' }}>
                                <th style={thLined}>Order Date</th>
                                <th style={thLined}>OrdNo</th>
                                <th style={thLined}>Customer</th>
                                <th className="data-figure" style={thLined}>Boxes</th>
                                <th style={{ ...thLined, borderRight: 'none' }}>Status after this design</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.ordList.filter((o) => openMode === 'all' || o.alone).map((o) => (
                                <tr key={o.key} style={{ borderBottom: BORDER }}>
                                  <td className="data-figure" style={tdLined}>{fmtDate(o.orderDate)}</td>
                                  <td className="data-figure" style={tdLined}>{o.orderNo}</td>
                                  <td style={{ ...tdLined, fontWeight: 600 }}>{o.customerName}</td>
                                  <td className="data-figure" style={tdLined}>{o.boxes.toLocaleString()}</td>
                                  <td style={{ ...tdLined, borderRight: 'none', color: o.alone ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                                    {o.alone ? 'Order complete' : 'Other designs still pending'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// PENDING-READY v1
/** Pending Order page top: 4 cards by how much of a customer's pending order already has a real batch (ready %).
 * Click a card -> its customers; click a customer -> the detail table below, filtered to that customer. */
const READY_BANDS = [
  { label: '0% Ready Order', sub: 'below 25% ready', lo: 0, hi: 25, color: 'var(--color-danger)' },
  { label: '25% Ready Order', sub: '25% to below 50%', lo: 25, hi: 50, color: 'var(--color-warning)' },
  { label: '50% Ready Order', sub: '50% to below 75%', lo: 50, hi: 75, color: 'var(--color-accent)' },
  { label: '75% Ready Order', sub: '75% and above', lo: 75, hi: 101, color: 'var(--color-success)' },
];

function PendingReadyCards({ groups, onPickCustomer }) {
  const [sel, setSel] = React.useState(null);
  const [q, setQ] = React.useState('');
  const rows = React.useMemo(() => {
    const m = new Map();
    (groups || []).forEach((g) => {
      const k = String(g.customerId);
      if (!m.has(k)) m.set(k, { id: k, name: g.customerName, ready: 0, total: 0, orders: 0 });
      const e = m.get(k);
      e.orders += 1;
      (g.lines || []).forEach((l) => {
        const b = Number(l.pack_box) || 0;
        e.total += b;
        if (!isNoBatchLine(l)) e.ready += b;
      });
    });
    return Array.from(m.values()).map((e) => ({ ...e, pct: e.total > 0 ? (e.ready / e.total) * 100 : 0 }));
  }, [groups]);
  const byBand = READY_BANDS.map((b) => rows.filter((r) => r.pct >= b.lo && r.pct < b.hi).sort((a, c) => c.pct - a.pct || c.total - a.total));
  const list = sel === null ? [] : byBand[sel].filter((r) => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase()));
  const fmtN = (n) => Number(n || 0).toLocaleString('en-IN');
  return (
    <div style={{ margin: 'var(--space-3) 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 'var(--space-3)' }}>
        {READY_BANDS.map((b, i) => (
          <button
            key={b.label}
            type="button"
            onClick={() => { setSel(sel === i ? null : i); setQ(''); }}
            style={{
              background: sel === i ? 'var(--color-accent-soft)' : 'var(--color-surface)',
              border: sel === i ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-3) var(--space-4)',
              cursor: 'pointer',
              textAlign: 'left',
              font: 'inherit',
            }}
          >
            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>{b.label}</div>
            <div className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: b.color, marginTop: 4 }}>{byBand[i].length}</div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>customers · {b.sub}</div>
          </button>
        ))}
      </div>
      {sel !== null && (
        <div style={{ marginTop: 'var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)', background: 'var(--color-surface)' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{READY_BANDS[sel].label} — {byBand[sel].length} customers</div>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find customer…"
            style={{ margin: '0 0 8px', padding: '6px 10px', width: '100%', maxWidth: 320, border: '1px solid var(--color-border)', borderRadius: 6, font: 'inherit' }}
          />
          <div className="table-scroll" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '6px 10px' }}>#</th>
                  <th style={{ padding: '6px 10px' }}>Customer</th>
                  <th className="data-figure" style={{ padding: '6px 10px', textAlign: 'right' }}>Ready %</th>
                  <th className="data-figure" style={{ padding: '6px 10px', textAlign: 'right' }}>Ready Box</th>
                  <th className="data-figure" style={{ padding: '6px 10px', textAlign: 'right' }}>Pending Box</th>
                  <th className="data-figure" style={{ padding: '6px 10px', textAlign: 'right' }}>Orders</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 && <tr><td colSpan={6} style={{ padding: 10, color: 'var(--color-text-muted)' }}>No customer in this card.</td></tr>}
                {list.map((r, i) => (
                  <tr key={r.id} style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border)' }} title="Click to see this customer's pending orders" onClick={() => onPickCustomer(r.name)}>
                    <td style={{ padding: '6px 10px' }}>{i + 1}</td>
                    <td style={{ padding: '6px 10px', fontWeight: 600 }}>{r.name}</td>
                    <td className="data-figure" style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700 }}>{r.pct.toFixed(0)}%</td>
                    <td className="data-figure" style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtN(r.ready)}</td>
                    <td className="data-figure" style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtN(r.total - r.ready)}</td>
                    <td className="data-figure" style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtN(r.orders)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
            Ready % = boxes that already have a real batch / all boxes in this customer&apos;s pending orders. Click a customer to see the orders below.
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrderAnalysisPage() {
  // Arrived from the Dashboard (Total Orders -> Customer Complete Order / Pending Order):
  // show just that detail table.
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const focus = routerLocation.state && routerLocation.state.focus;
  const focusMode = focus === 'complete' || focus === 'pending' || focus === 'orders';
  const [pending, setPending] = useState([]);
  const [dimension, setDimension] = useState('customer');
  const [breakdown, setBreakdown] = useState([]);
  const [sizeCards, setSizeCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [designFilter, setDesignFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [designOptions, setDesignOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [completeOrders, setCompleteOrders] = useState([]);
  const [pendingOrderGroups, setPendingOrderGroups] = useState([]);
  const [showCompleteDetail, setShowCompleteDetail] = useState(focus === 'complete');
  const [showPendingDetail, setShowPendingDetail] = useState(focus === 'pending');
  const [showDesignNeed, setShowDesignNeed] = useState(false);

  // Find-box queries for the "Customer Complete Order" detail table —
  // one per column, all optional, all combined with AND.
  const [completeOrderDateQ, setCompleteOrderDateQ] = useState('');
  const [completeCompleteDateQ, setCompleteCompleteDateQ] = useState('');
  const [completeOrderNoQ, setCompleteOrderNoQ] = useState('');
  const [completeCustomerQ, setCompleteCustomerQ] = useState('');
  const [completeDesignQ, setCompleteDesignQ] = useState('');
  const [completeBatchQ, setCompleteBatchQ] = useState('');
  const [completeRefPartyQ, setCompleteRefPartyQ] = useState('');

  // Same, for the "Pending Order" detail table (no Order Complete Date column).
  const [pendOrderDateQ, setPendOrderDateQ] = useState('');
  const [pendOrderNoQ, setPendOrderNoQ] = useState('');
  const [pendCustomerQ, setPendCustomerQ] = useState('');
  const [pendDesignQ, setPendDesignQ] = useState('');
  const [pendBatchQ, setPendBatchQ] = useState('');
  const [pendRefPartyQ, setPendRefPartyQ] = useState('');

  const loadPending = () => {
    const params = {};
    if (designFilter) params.design = designFilter;
    if (sizeFilter) params.size = sizeFilter;
    return orderApi.listPending(params).then(setPending);
  };
  const loadBreakdown = (dim) => orderApi.getBreakdown(dim).then(setBreakdown);
  // Both "Customer Complete Order" and "Pending Order" cards are driven by
  // order-number groups now — complete when EVERY line of an order number
  // has a real production batch, pending when at least one line doesn't.
  // Unrelated to the `pending` state above, which stays the original
  // ordered-vs-dispatched-quantity list for the lower "Pending Orders"
  // table (Aging/Trend) further down the page.
  const loadOrderGroups = () => {
    orderApi.getCompleteOrders().then(setCompleteOrders).catch(() => {});
    orderApi.getPendingOrderGroups().then(setPendingOrderGroups).catch(() => {});
  };
  const loadSizeCards = () => orderApi.getBreakdown('size').then(setSizeCards);

  // Design/Size dropdown option lists — loaded once; they reflect every
  // design/size with a pending order, not just what the active filter
  // shows, so choosing one can broaden the view rather than only narrow it.
  useEffect(() => {
    orderApi.getDesignOptions().then(setDesignOptions).catch(() => {});
    orderApi.getSizeOptions().then(setSizeOptions).catch(() => {});
    loadSizeCards().catch(() => {});
    loadOrderGroups();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadPending()
      .catch((err) => setError(err.message || 'Could not load pending orders.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designFilter, sizeFilter]);

  useEffect(() => {
    loadBreakdown(dimension).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension]);

  const handleSort = (key) => {
    setSort((prev) => (prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }));
  };

  const sortedPending = React.useMemo(() => {
    if (!sort.key) return pending;
    const copy = [...pending];
    copy.sort((a, b) => {
      const av = (a[sort.key] || '').toString();
      const bv = (b[sort.key] || '').toString();
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      return sort.direction === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [pending, sort]);

  // Filtered order groups for each card, applied BEFORE size columns/totals
  // are computed, so everything below (columns, Grand Total row) reflects
  // only what the find boxes currently match.
  const filteredCompleteOrders = React.useMemo(() => filterOrderGroups(completeOrders, {
    orderDate: completeOrderDateQ,
    completeDate: completeCompleteDateQ,
    orderNo: completeOrderNoQ,
    customer: completeCustomerQ,
    design: completeDesignQ,
    batchShade: completeBatchQ,
    refParty: completeRefPartyQ,
  }), [
    completeOrders, completeOrderDateQ, completeCompleteDateQ, completeOrderNoQ,
    completeCustomerQ, completeDesignQ, completeBatchQ, completeRefPartyQ,
  ]);

  const filteredPendingOrderGroups = React.useMemo(() => filterOrderGroups(pendingOrderGroups, {
    orderDate: pendOrderDateQ,
    orderNo: pendOrderNoQ,
    customer: pendCustomerQ,
    design: pendDesignQ,
    batchShade: pendBatchQ,
    refParty: pendRefPartyQ,
  }), [
    pendingOrderGroups, pendOrderDateQ, pendOrderNoQ, pendCustomerQ, pendDesignQ, pendBatchQ, pendRefPartyQ,
  ]);

  // Dynamic size columns and grand totals (overall + per size) for the two
  // card detail tables — computed from the FILTERED groups' lines, so they
  // always match exactly what the table currently shows.
  const completeSizeNames = React.useMemo(() => distinctSizeNamesFromGroups(filteredCompleteOrders), [filteredCompleteOrders]);
  const completeLineTotals = React.useMemo(() => {
    const totals = {};
    completeSizeNames.forEach((s) => { totals[s] = 0; });
    let grand = 0;
    filteredCompleteOrders.forEach((g) => g.lines.forEach((l) => {
      totals[l.size_name] = (totals[l.size_name] || 0) + Number(l.pack_box || 0);
      grand += Number(l.pack_box || 0);
    }));
    return { bySize: totals, grand };
  }, [filteredCompleteOrders, completeSizeNames]);

  const pendingSizeNames = React.useMemo(() => distinctSizeNamesFromGroups(filteredPendingOrderGroups), [filteredPendingOrderGroups]);
  const pendingLineTotals = React.useMemo(() => {
    const totals = {};
    pendingSizeNames.forEach((s) => { totals[s] = 0; });
    let grand = 0;
    filteredPendingOrderGroups.forEach((g) => g.lines.forEach((l) => {
      totals[l.size_name] = (totals[l.size_name] || 0) + Number(l.pack_box || 0);
      grand += Number(l.pack_box || 0);
    }));
    return { bySize: totals, grand };
  }, [filteredPendingOrderGroups, pendingSizeNames]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await orderApi.refreshOrderAnalysis();
      await loadPending();
      await loadBreakdown(dimension);
      await loadSizeCards();
      loadOrderGroups();
      orderApi.getDesignOptions().then(setDesignOptions).catch(() => {});
      orderApi.getSizeOptions().then(setSizeOptions).catch(() => {});
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      {focusMode && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text-primary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
          >
            ← Back to Dashboard
          </button>
          <button
            type="button"
            onClick={() => navigate('/order-analysis', { replace: true })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', font: 'inherit', fontSize: 'var(--font-size-sm)', textDecoration: 'underline' }}
          >
            Open full Order Analysis
          </button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>{focus === 'orders' ? 'Total Orders' : focus === 'complete' ? 'Customer Complete Order' : focus === 'pending' ? 'Pending Order' : 'Party Order Analysis'}</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Pending orders, aging, trend, and fulfillment by customer, location, and product
            attribute.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh analysis'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {(!focusMode || focus === 'orders') && (
      <div style={{ display: 'flex', gap: 'var(--space-3)', margin: 'var(--space-3) 0', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setShowCompleteDetail((v) => !v)}
          style={{
            background: showCompleteDetail ? 'var(--color-accent-soft)' : 'var(--color-surface)',
            border: showCompleteDetail ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-3) var(--space-5)',
            minWidth: 200,
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Customer Complete Order</div>
          <div className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-success)', marginTop: 4 }}>
            {completeOrders.length}
          </div>
        </button>
        <button
          type="button"
          onClick={() => setShowPendingDetail((v) => !v)}
          style={{
            background: showPendingDetail ? 'var(--color-accent-soft)' : 'var(--color-surface)',
            border: showPendingDetail ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-3) var(--space-5)',
            minWidth: 200,
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Pending Order</div>
          <div className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-warning)', marginTop: 4 }}>
            {pendingOrderGroups.length}
          </div>
        </button>
        <button
          type="button"
          onClick={() => navigate('/order-analysis/design-need')}
          style={{
            background: showDesignNeed ? 'var(--color-accent-soft)' : 'var(--color-surface)',
            border: showDesignNeed ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-3) var(--space-5)',
            minWidth: 200,
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Design Need To Complete Pending Order</div>
          <div className="data-figure" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-accent)', marginTop: 4 }}>
            {new Set(pendingOrderGroups.flatMap((g) => g.lines.filter(isNoBatchLine).map((l) => l.design_name))).size}
          </div>
        </button>
      </div>

      )}

      {showCompleteDetail && (
        <Card title="Customer Complete Order — Detail">
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#e2e8f0' }}>
                  <FindableHeader label="Order Date" value={completeOrderDateQ} onChange={setCompleteOrderDateQ} />
                  <FindableHeader label="Order Complete Date" value={completeCompleteDateQ} onChange={setCompleteCompleteDateQ} />
                  <FindableHeader label="OrdNo" value={completeOrderNoQ} onChange={setCompleteOrderNoQ} />
                  <FindableHeader label="Customer" value={completeCustomerQ} onChange={setCompleteCustomerQ} />
                  <FindableHeader label="Design" value={completeDesignQ} onChange={setCompleteDesignQ} />
                  <FindableHeader label="Batch/Shade" value={completeBatchQ} onChange={setCompleteBatchQ} />
                  {completeSizeNames.map((s) => (
                    <th key={s} className="data-figure" style={thLined}>{s}</th>
                  ))}
                  <th className="data-figure" style={thLined}>Total</th>
                  <th className="data-figure" style={thLined}>Weight</th>
                  <th className="data-figure" style={thLined}>G. Total Box &amp; Weight</th>
                  <FindableHeader label="Ref. Party" value={completeRefPartyQ} onChange={setCompleteRefPartyQ} lastColumn />
                </tr>
              </thead>
              <tbody>
                {filteredCompleteOrders.map((group) => group.lines.map((line, lineIdx) => (
                  <tr key={`${group.orderNo}-${group.customerId}-${lineIdx}`} style={{ borderBottom: BORDER }}>
                    {lineIdx === 0 && (
                      <>
                        <td rowSpan={group.lines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top' }}>{fmtDate(group.orderDate)}</td>
                        <td rowSpan={group.lines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top' }}>{fmtDate(group.completeDate)}</td>
                        <td rowSpan={group.lines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top' }}>{group.orderNo}</td>
                        <td rowSpan={group.lines.length} style={{ ...tdLined, verticalAlign: 'top', fontWeight: 600 }}>{group.customerName}</td>
                      </>
                    )}
                    <td style={tdLined}>{line.design_name}</td>
                    <td style={tdLined}>{line.batch_shade || '—'}</td>
                    {completeSizeNames.map((s) => (
                      <td key={s} className="data-figure" style={tdLined}>
                        {line.size_name === s ? line.pack_box : 0}
                      </td>
                    ))}
                    <td className="data-figure" style={{ ...tdLined, fontWeight: 600, color: 'var(--color-accent)' }}>{line.pack_box}</td>
                    <td className="data-figure" style={tdLined}>{line.lineWeight !== null && line.lineWeight !== undefined ? `${line.lineWeight} Kg` : '—'}</td>
                    {lineIdx === 0 && (
                      <td rowSpan={group.lines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top', fontWeight: 600 }}>
                        {group.groupTotalBox} Box{group.groupTotalWeight ? ` / ${group.groupTotalWeight} Kg` : ''}
                      </td>
                    )}
                    {lineIdx === 0 && (
                      <td rowSpan={group.lines.length} style={{ ...tdLined, verticalAlign: 'top', borderRight: 'none' }}>{group.refPartyName || '—'}</td>
                    )}
                  </tr>
                )))}
                {filteredCompleteOrders.length === 0 && (
                  <tr>
                    <td colSpan={10 + completeSizeNames.length} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                      {completeOrders.length === 0 ? 'No order is fully complete yet.' : 'No rows match this search.'}
                    </td>
                  </tr>
                )}
                {filteredCompleteOrders.length > 0 && (
                  <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
                    <td colSpan={6} style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', textAlign: 'right', borderRight: BORDER }}>
                      Grand Total
                    </td>
                    {completeSizeNames.map((s) => (
                      <td key={s} className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER }}>
                        {completeLineTotals.bySize[s]}
                      </td>
                    ))}
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER }}>
                      {completeLineTotals.grand}
                    </td>
                    <td style={{ padding: '8px 4px', borderRight: BORDER }} />
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER }}>
                      {filteredCompleteOrders.reduce((sum, g) => sum + (Number(g.groupTotalBox) || 0), 0)} Box
                      {' / '}
                      {Math.round(filteredCompleteOrders.reduce((sum, g) => sum + (Number(g.groupTotalWeight) || 0), 0) * 10) / 10} Kg
                    </td>
                    <td style={{ padding: '8px 4px' }} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}


      {showPendingDetail && (
        <PendingReadyCards
          groups={pendingOrderGroups}
          onPickCustomer={(name) => {
            setPendCustomerQ(name);
            setTimeout(() => { const el = document.getElementById('pending-detail'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
          }}
        />
      )}
      {showPendingDetail && <div id="pending-detail" />}
      {showPendingDetail && (
        <Card title="Pending Order — Detail">
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', border: BORDER }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#e2e8f0' }}>
                  <FindableHeader label="Order Date" value={pendOrderDateQ} onChange={setPendOrderDateQ} />
                  <FindableHeader label="OrdNo" value={pendOrderNoQ} onChange={setPendOrderNoQ} />
                  <FindableHeader label="Customer" value={pendCustomerQ} onChange={setPendCustomerQ} />
                  <FindableHeader label="Design" value={pendDesignQ} onChange={setPendDesignQ} />
                  <FindableHeader label="Batch/Shade" value={pendBatchQ} onChange={setPendBatchQ} />
                  {pendingSizeNames.map((s) => (
                    <th key={s} className="data-figure" style={thLined}>{s}</th>
                  ))}
                  <th className="data-figure" style={thLined}>Total</th>
                  <th className="data-figure" style={thLined}>Weight</th>
                  <th className="data-figure" style={thLined}>G. Total Box &amp; Weight</th>
                  <FindableHeader label="Ref. Party" value={pendRefPartyQ} onChange={setPendRefPartyQ} lastColumn />
                </tr>
              </thead>
              <tbody>
                {filteredPendingOrderGroups.map((group) => group.lines.map((line, lineIdx) => (
                  <tr key={`${group.orderNo}-${group.customerId}-${lineIdx}`} style={{ borderBottom: BORDER }}>
                    {lineIdx === 0 && (
                      <>
                        <td rowSpan={group.lines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top' }}>{fmtDate(group.orderDate)}</td>
                        <td rowSpan={group.lines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top' }}>{group.orderNo}</td>
                        <td rowSpan={group.lines.length} style={{ ...tdLined, verticalAlign: 'top', fontWeight: 600 }}>{group.customerName}</td>
                      </>
                    )}
                    <td style={tdLined}>{line.design_name}</td>
                    <td
                      style={{
                        ...tdLined,
                        color: !line.batch_shade || String(line.batch_shade).trim().toUpperCase() === '0/A' ? 'var(--color-danger)' : 'var(--color-text-primary)',
                        fontWeight: !line.batch_shade || String(line.batch_shade).trim().toUpperCase() === '0/A' ? 600 : 400,
                      }}
                    >
                      {line.batch_shade || '0/A'}
                    </td>
                    {pendingSizeNames.map((s) => (
                      <td key={s} className="data-figure" style={tdLined}>
                        {line.size_name === s ? line.pack_box : 0}
                      </td>
                    ))}
                    <td className="data-figure" style={{ ...tdLined, fontWeight: 600, color: 'var(--color-accent)' }}>{line.pack_box}</td>
                    <td className="data-figure" style={tdLined}>
                      {line.lineWeight !== null && line.lineWeight !== undefined ? `${line.lineWeight} Kg (approx)` : '—'}
                    </td>
                    {lineIdx === 0 && (
                      <td rowSpan={group.lines.length} className="data-figure" style={{ ...tdLined, verticalAlign: 'top', fontWeight: 600 }}>
                        {group.groupTotalBox} Box{group.groupTotalWeight ? ` / ${group.groupTotalWeight} Kg (approx)` : ''}
                      </td>
                    )}
                    {lineIdx === 0 && (
                      <td rowSpan={group.lines.length} style={{ ...tdLined, verticalAlign: 'top', borderRight: 'none' }}>{group.refPartyName || '—'}</td>
                    )}
                  </tr>
                )))}
                {filteredPendingOrderGroups.length === 0 && (
                  <tr>
                    <td colSpan={9 + pendingSizeNames.length} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                      {pendingOrderGroups.length === 0 ? 'No pending orders.' : 'No rows match this search.'}
                    </td>
                  </tr>
                )}
                {filteredPendingOrderGroups.length > 0 && (
                  <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
                    <td colSpan={5} style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', textAlign: 'right', borderRight: BORDER }}>
                      Grand Total
                    </td>
                    {pendingSizeNames.map((s) => (
                      <td key={s} className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER }}>
                        {pendingLineTotals.bySize[s]}
                      </td>
                    ))}
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER }}>
                      {pendingLineTotals.grand}
                    </td>
                    <td style={{ padding: '8px 4px', borderRight: BORDER }} />
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', borderRight: BORDER }}>
                      {filteredPendingOrderGroups.reduce((sum, g) => sum + (Number(g.groupTotalBox) || 0), 0)} Box
                      {' / '}
                      {Math.round(filteredPendingOrderGroups.reduce((sum, g) => sum + (Number(g.groupTotalWeight) || 0), 0) * 10) / 10} Kg
                    </td>
                    <td style={{ padding: '8px 4px' }} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 8 }}>
            An order stays here until EVERY one of its design lines has a real production batch —
            lines shown in red still show &quot;0/A&quot;, meaning that piece hasn&apos;t been
            produced yet. Weight for a &quot;0/A&quot; line is an approximation, based on the
            most recent known weight for that design and size.
          </p>
        </Card>
      )}

      <InactiveCustomersCard title="90+ Days Inactive Customers" thresholdDays={90} />
      <InactiveCustomersCard title="180+ Days Inactive Customers" thresholdDays={180} />
      <InactiveCustomersCard title="1 Year+ Inactive Customers" thresholdDays={365} />

      {!focusMode && (
      <>
      {sizeCards.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', margin: 'var(--space-3) 0' }}>
          {sizeCards.map((s) => (
            <div
              key={s.id}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--space-3) var(--space-4)',
                minWidth: 140,
              }}
            >
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{s.name}</div>
              <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
                {s.pendingQty === null ? 0 : s.pendingQty}
              </div>
            </div>
          ))}
          <div
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-accent)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-3) var(--space-4)',
              minWidth: 140,
            }}
          >
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Total (all sizes)</div>
            <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-accent)' }}>
              {sizeCards.reduce((sum, s) => sum + (Number(s.pendingQty) || 0), 0)}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-3)', margin: 'var(--space-3) 0', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Design
          <select value={designFilter} onChange={(e) => setDesignFilter(e.target.value)} style={{ ...inputStyle, width: 220 }}>
            <option value="">All designs</option>
            {designOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Size
          <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)} style={{ ...inputStyle, width: 160 }}>
            <option value="">All sizes</option>
            {sizeOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        {(designFilter || sizeFilter) && (
          <Button variant="text" onClick={() => { setDesignFilter(''); setSizeFilter(''); }} style={{ alignSelf: 'flex-end' }}>
            Clear filters
          </Button>
        )}
      </div>

      <Card title="Pending Orders">
        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        ) : (
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                <SortableHeader label="Design" sortKey="design_name" activeSort={sort} onSort={handleSort} />
                <SortableHeader label="Size" sortKey="size_name" activeSort={sort} onSort={handleSort} />
                {['Customer', 'City', 'Pending Qty', 'Aging', 'Trend'].map((h) => (
                  <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPending.map((row) => (
                <tr key={row.summary_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.design_name}</td>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.size_name || '—'}</td>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.customer_name}</td>
                  <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{row.city}</td>
                  <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                    {row.pending_qty}
                  </td>
                  <td
                    className="data-figure"
                    style={{
                      padding: '8px 4px',
                      fontSize: 'var(--font-size-sm)',
                      color: row.order_aging_days > 60 ? 'var(--color-danger)' : row.order_aging_days > 30 ? 'var(--color-warning)' : 'var(--color-text-primary)',
                    }}
                  >
                    {row.order_aging_days}d
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <TrendBadge direction={row.order_trend_direction} />
                  </td>
                </tr>
              ))}
              {sortedPending.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                    No pending orders — everything ordered has been dispatched.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <Card
        title="Breakdown"
        actions={
          <select value={dimension} onChange={(e) => setDimension(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
            {DIMENSIONS.map((d) => (
              <option key={d.key} value={d.key}>{d.label}-wise</option>
            ))}
          </select>
        }
      >
        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
              {['Name', 'Ordered Qty', 'Orders', 'Pending Qty', 'Fulfillment %'].map((h) => (
                <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {breakdown.map((b) => (
              <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.name}</td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.orderedQty}</td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{b.orderCount}</td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>
                  {b.pendingQty === null ? '—' : b.pendingQty}
                </td>
                <td
                  className="data-figure"
                  style={{
                    padding: '8px 4px',
                    fontSize: 'var(--font-size-sm)',
                    color: b.fulfillmentPct === null ? 'var(--color-text-muted)' : b.fulfillmentPct >= 90 ? 'var(--color-success)' : b.fulfillmentPct >= 50 ? 'var(--color-warning)' : 'var(--color-danger)',
                  }}
                >
                  {b.fulfillmentPct === null ? '—' : `${b.fulfillmentPct}%`}
                </td>
              </tr>
            ))}
            {breakdown.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No data.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>
      </>
      )}
    </div>
  );
}
